import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, X } from 'lucide-react';

const ModernMultiSelect = ({ value = [], options, onChange, placeholder, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (val) => {
        const isSelected = value.includes(val);
        let newValue;
        if (isSelected) {
            newValue = value.filter(v => v !== val);
        } else {
            newValue = [...value, val];
        }
        onChange(newValue);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange([]);
    };

    const displayLabel = value.length === 0
        ? placeholder
        : value.length === 1
            ? options.find(o => (o.value || o) === value[0])?.label || value[0]
            : `${value.length} sélectionnés`;

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-full hover:border-blue-300 focus:ring-4 focus:ring-blue-100 transition-all active:scale-[0.98] shadow-sm"
            >
                <span className="truncate max-w-[120px]">{displayLabel}</span>
                <div className="flex items-center gap-1">
                    {value.length > 0 && (
                        <div
                            onClick={handleClear}
                            className="p-0.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-colors mr-1"
                        >
                            <X size={14} />
                        </div>
                    )}
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-slate-300/30 z-50 overflow-hidden min-w-[200px]"
                    >
                        <div className="max-h-60 overflow-y-auto py-2">
                            {options.map((opt, i) => {
                                const val = opt.value !== undefined ? opt.value : opt;
                                const label = opt.label !== undefined ? opt.label : opt;
                                const isSelected = value.includes(val);
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => handleSelect(val)}
                                        className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-all border-b border-slate-50 last:border-0 flex items-center justify-between
                                            ${isSelected
                                                ? 'bg-blue-50 text-blue-600 font-bold'
                                                : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                                            }`}
                                    >
                                        <span className="truncate mr-2">{label}</span>
                                        {isSelected && <Check size={14} className="flex-shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ModernMultiSelect;
